<template>
    <q-table
        :title="title"
        :data="data"
        :columns="columns"
        :loading="loading"
        row-key="id"
        flat
        :hide-bottom="hideBottom"
    >
        <template v-slot:top>
            <span class="text-weight-bold text-h6">{{ title }}</span>
        </template>
        <!-- table rows -->
        <template v-slot:body="props">
            <q-tr :props="props" :class="getRowClass(props.row)">
                <q-td v-for="col in props.cols" :key="col.name" :props="props">
                    <template v-if="col.name === 'title'">
                        {{
                            getTranslationFromJSONString(
                                props.row.title,
                                locale.key
                            )
                        }}
                    </template>
                    <template v-else-if="col.name === 'price'">
                        <span
                            :class="[{ 'text-strike': props.row.on_promotion }]"
                        >
                            {{
                                new Intl.NumberFormat(
                                    currency.definition['locale'],
                                    {
                                        style: 'currency',
                                        currency: currency.key,
                                    }
                                ).format(
                                    parseFloat(
                                        JSON.parse(props.row.price)[
                                            currency.key
                                        ]
                                    )
                                )
                            }}
                        </span>
                        <span
                            v-if="props.row.on_promotion"
                            class="text-secondary q-ml-md"
                            >{{
                                new Intl.NumberFormat(
                                    currency.definition['locale'],
                                    {
                                        style: 'currency',
                                        currency: currency.key,
                                    }
                                ).format(discountedPrice(props.row))
                            }}</span
                        >
                    </template>
                    <template v-else-if="col.name === 'discount'">
                        <span
                            :class="[
                                { 'text-secondary': props.row.on_promotion },
                            ]"
                        >
                            {{
                                parseFloat(
                                    props.row.promotion_discount
                                ).toFixed(2)
                            }}
                        </span>
                    </template>
                    <template v-else-if="col.name === 'comercial_discount'">
                        <span>
                            {{
                                parseFloat(
                                    props.row.row_discount_percentage
                                ).toFixed(2)
                            }}
                        </span>
                    </template>
                    <template v-else-if="col.name === 'preferred_class_id'">
                        {{ props.row.preferred_class_id || '--' }}
                    </template>
                    <template v-else-if="col.name === 'quantity'">
                        {{ props.row.quantity }}
                    </template>
                    <template v-else-if="col.name === 'quantity_available'">
                        <q-icon
                            v-if="hasQuantityAlert(props.row)"
                            class="q-ml-sm fade-in-out"
                            name="close"
                            size="sm"
                        ></q-icon>
                        <span>
                            {{ props.row.quantity_available }}
                        </span>
                    </template>
                    <template v-else-if="col.name === 'vat'">
                        {{ props.row.vat.value.toFixed(2) }}
                    </template>
                    <template v-else-if="col.name === 'total'">
                        {{
                            new Intl.NumberFormat(
                                currency.definition['locale'],
                                {
                                    style: 'currency',
                                    currency: currency.key,
                                }
                            ).format(parseFloat(totalPriceWithVat(props.row)))
                        }}
                    </template>
                    <template v-else-if="col.name === 'row_total'">
                        {{
                            new Intl.NumberFormat(
                                currency.definition['locale'],
                                {
                                    style: 'currency',
                                    currency: currency.key,
                                }
                            ).format(
                                parseFloat(
                                    JSON.parse(props.row.row_total)[
                                        currency.key
                                    ]
                                )
                            )
                        }}
                    </template>
                </q-td>
            </q-tr>
        </template>
        <!-- bottom row -->
        <template v-slot:bottom>
            <div
                class="row col-12 justify-end text-subtitle text-body1 text-weight-bold"
            >
                {{
                    new Intl.NumberFormat(currency.definition['locale'], {
                        style: 'currency',
                        currency: currency.key,
                    }).format(parseFloat(cartTotal))
                }}
            </div>
        </template>
    </q-table>
</template>

<script>
import store from '@/store'
import translate from '@/local-shared/mixins/translate'
import _ from 'lodash'

export default {
    name: 'CheckoutItemsTableComponent',

    mixins: [translate],

    props: {
        loading: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: null,
        },
        columns: {
            type: Array,
            default: () => [],
        },
        data: {
            type: Array,
            default: () => [],
        },
        hideBottom: {
            type: Boolean,
            default: false,
        },
        inContract: {
            type: Array,
            default: () => [],
        },
        contract: {
            type: Boolean,
            default: false,
        },
        selectedContract: {
            type: Object,
            default: () => {},
        },
    },

    computed: {
        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        cartTotal() {
            let total = 0
            this.data.forEach(
                (cartItem) =>
                    (total =
                        total + parseFloat(this.totalPriceWithVat(cartItem)))
            )
            return total
        },

        emptySelectedContract() {
            return _.isEmpty(this.selectedContract)
        },
    },

    methods: {
        discountedPrice(item) {
            let price = 0
            if (this.currency) {
                price = parseFloat(JSON.parse(item.price)[this.currency.key])

                price = price * (1 - item.promotion_discount / 100)
            }
            return price.toFixed(2)
        },

        totalPriceWithVat(item) {
            let price = 0

            if (this.currency) {
                price =
                    parseFloat(JSON.parse(item.price)[this.currency.key]) *
                    item.quantity

                price = price * (1 - item.promotion_discount / 100) // apply promotion discount

                if (!this.emptySelectedContract) {
                    // apply contract line discount
                    price = price * (1 - item.row_discount_percentage / 100)
                    // apply contract global discount
                    price =
                        price *
                        (1 -
                            this.selectedContract.global_discount_percentage /
                                100)
                }

                price = price * (1 + parseInt(item.vat.value) / 100) // apply vat on the discounted/not price
            }
            return price.toFixed(2)
        },

        getRowClass(row) {
            if (this.contract) {
                const found = this.inContract.find((item) => item.id === row.id)
                if (found) {
                    return 'in-contract'
                } else {
                    return 'not-in-contract'
                }
            }
            return ''
        },

        hasQuantityAlert(row) {
            const found = this.inContract.find(
                (item) => item.id === row.course_id
            )
            if (found && found.quantity > row.quantity_available) {
                return true
            }
            return false
        },
    },
}
</script>
